import type { JSX } from "react";

import type {
  RecommendationFilterOption,
  RecommendationFilterSpec,
} from "../hooks/useRecommendationFilters";
import RecommendationFilterSelect from "./RecommendationFilterSelect";

interface RecommendationFilterBarProps<T> {
  /** The dropdown descriptors, in display order. */
  filters: RecommendationFilterSpec<T>[];
  /** Selected value per filter key. */
  values: Record<string, string>;
  /** Options with live counts per filter key. */
  options: Record<string, RecommendationFilterOption[]>;
  /** Whether to offer the Clear filters shortcut. */
  hasActiveFilter: boolean;
  onChange: (key: string, value: string) => void;
  onClear: () => void;
  /** How many items survive the current filters. */
  shownCount: number;
  /** How many items there are in total. */
  totalCount: number;
  /** Plural noun for the count line, e.g. "recommendations". */
  noun: string;
}

/**
 * The filter row above a recommendations grid: one dropdown per filter, a Clear shortcut, and the
 * "Showing X of Y" count.
 *
 * Shared by the AI Audit and Persona Recommendations tabs so the two read as one screen. Everything
 * it renders comes from the descriptor list, so adding a filter is a one-entry change at the call
 * site.
 */
const RecommendationFilterBar = <T,>({
  filters,
  values,
  options,
  hasActiveFilter,
  onChange,
  onClear,
  shownCount,
  totalCount,
  noun,
}: RecommendationFilterBarProps<T>): JSX.Element => (
  <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
    <div className="flex items-end gap-3 flex-wrap">
      {filters.map(({ key, label }) => (
        <RecommendationFilterSelect
          key={key}
          label={label}
          value={values[key]}
          options={options[key]}
          onChange={(value) => onChange(key, value)}
        />
      ))}
      {hasActiveFilter && (
        <button
          onClick={onClear}
          className="text-sm font-semibold text-primary hover:underline py-2"
        >
          Clear filters
        </button>
      )}
    </div>
    <span className="text-sm text-on-surface-variant">
      Showing {shownCount} of {totalCount} {noun}
    </span>
  </div>
);

export default RecommendationFilterBar;
